import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'

import getIsExperienceLoyaltyBurnEnabled from 'loyalty/selectors/getIsExperienceLoyaltyBurnEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import useOfferPageBurnPoints from 'hooks/useOfferPageBurnPoints'
import ExperiencePageDispatchContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageDispatchContext'
import { ExperienceOfferPageActions } from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateReducer'
import LoyaltyBurnSliderBase from 'loyalty/components/LoyaltyBurnSliderBase'
import useOfferBurnSliderState from 'loyalty/hooks/useOfferBurnSliderState'

interface Props {
  /** Price (cash) of the currently selected ticket(s) to base the burn range on. */
  price: number
  loyaltyProductType: App.LoyaltyProductType
}

function ExperienceOfferPageLoyaltyPointsSlider(props: Props) {
  const { price, loyaltyProductType } = props

  const intl = useIntl()
  const maxPointsTooltipDescription = intl.formatMessage({
    id: 'Wyix0WkKm43-6wfcWD77',
    defaultMessage: 'The maximum points is based on the highest priced ticket within your selected session.',
  })

  const experienceDispatch = useContext(ExperiencePageDispatchContext)
  const isLoyaltyEnabled = useAppSelector(getIsExperienceLoyaltyBurnEnabled)
  const searchBurnEnabled = useAppSelector(state => state.loyalty.hasSelectedBurnToggle)

  const burnResult = useOfferPageBurnPoints(price, loyaltyProductType, undefined, { disabled: price <= 0 })

  // Ticket count changes alter the price basis, which re-triggers the burn calculation
  // request and briefly clears priceInPoints. Hold onto the last known value so the
  // slider doesn't flash to a loading state / full balance while the new price's
  // calculation is in flight.
  const lastPriceInPointsRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (burnResult.priceInPoints !== undefined) {
      lastPriceInPointsRef.current = burnResult.priceInPoints
    }
  }, [burnResult.priceInPoints])
  const effectivePriceInPoints = burnResult.priceInPoints ?? lastPriceInPointsRef.current

  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const maxBurnPoints = useMemo(() => {
    const priceInPoints = effectivePriceInPoints ?? 0
    return priceInPoints > 0 ? Math.min(userPoints, priceInPoints) : userPoints
  }, [effectivePriceInPoints, userPoints])

  const isLoadingMax = searchBurnEnabled && effectivePriceInPoints === undefined

  const onPointsToApplyChange = useCallback((pointsToApply: number | undefined) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setLoyaltyPointsToApply, pointsToApply })
  }, [experienceDispatch])

  const { shouldShow, sliderProps } = useOfferBurnSliderState({
    isLoyaltyEnabled,
    maxBurnPoints,
    isLoadingMax,
    onPointsToApplyChange,
    // Ticket count changes alter maxBurnPoints; preserve the user's chosen amount
    // instead of always snapping back to the new max.
    stickyUserChoice: true,
  })

  if (!shouldShow) {
    return null
  }

  return <LoyaltyBurnSliderBase
    {...sliderProps}
    maxPointsTooltipDescription={maxPointsTooltipDescription}
  />
}

export default ExperienceOfferPageLoyaltyPointsSlider
